import { useState, type ReactNode } from "react";
import { BotonPreguntaleAtlas } from "../../chat-atlas";
import { PasoConexiones } from "./components/PasoConexiones";
import { PasoMetaAds } from "./components/PasoMetaAds";
import { PasoSistemaDiseno } from "./components/PasoSistemaDiseno";
import type { PropsPasoPuestaEnMarcha } from "./components/PieDePaso";
import {
  PASOS_PUESTA_EN_MARCHA,
  pasoAnterior,
  pasoSiguiente,
  progresoDePaso,
  type IdPasoPuestaEnMarcha,
} from "./datos/pasosPuestaEnMarcha";

/**
 * "Puesta en marcha": el flujo por el que se lleva al usuario para dejar el
 * programa andando.
 *
 * ES UN CONTENEDOR DE PASOS AUNQUE HOY HAYA UNO SOLO. Esta pantalla no sabe
 * nada de conexiones: sabe en qué paso está el usuario, cuántos hay y cuál
 * viene después, y todo eso lo DERIVA de `PASOS_PUESTA_EN_MARCHA`
 * (`datos/pasosPuestaEnMarcha.ts`). El indicador de progreso se dibuja con
 * `progreso.numero`/`progreso.total`: no hay un "Paso 1 de 1" escrito en el
 * JSX, que es justo el lugar donde el paso 2 se agrega y el indicador sigue
 * mintiendo. Sumar el paso 2 es sumar un objeto a la lista y una entrada al
 * registro de abajo — no rehacer esta pantalla.
 *
 * EL PASO VIVE EN ESTADO, NO EN LA URL. Los pasos de una puesta en marcha no
 * son destinos que uno comparta o marque como favorito, y un `/onboarding/2`
 * abierto de cero mostraría el paso 2 de un flujo que nunca se empezó. Cuando
 * haga falta retomar donde se dejó, lo que hay que guardar es el ID del paso
 * (que por eso no es el índice), no la URL.
 *
 * CÓMO SE LLEGA ACÁ. Hoy, por el ítem "Puesta en marcha" de la sidebar, que es
 * ANDAMIAJE para poder entrar a probar: el disparador real, cuando esto se
 * active, va a ser el primer ingreso del usuario. Ver el comentario del ítem en
 * `components/Sidebar.tsx`.
 *
 * Ver ADR-2026-08-17-estudio-marketing-puesta-en-marcha.
 */

/**
 * Qué componente dibuja cada paso. `Record` sobre el union de ids: agregar un
 * paso a la lista sin agregarlo acá NO compila, así que no hay forma de dejar
 * un paso en el indicador y una pantalla en blanco abajo.
 */
const COMPONENTE_PASO: Record<IdPasoPuestaEnMarcha, (props: PropsPasoPuestaEnMarcha) => ReactNode> =
  {
    conexiones: (props) => <PasoConexiones {...props} />,
    "sistema-diseno": (props) => <PasoSistemaDiseno {...props} />,
    "meta-ads": (props) => <PasoMetaAds {...props} />,
  };

export const PuestaEnMarchaPage = () => {
  const [idPaso, setIdPaso] = useState<IdPasoPuestaEnMarcha>(PASOS_PUESTA_EN_MARCHA[0].id);

  const progreso = progresoDePaso(PASOS_PUESTA_EN_MARCHA, idPaso);
  // Inalcanzable mientras el id salga de la lista (arranca en el primero y sólo
  // se mueve a un paso que `pasoSiguiente` devolvió). Va igual: el día que el
  // paso se lea de algo guardado, un id viejo tiene que dar pantalla vacía y no
  // un "Paso 0 de 3".
  if (!progreso) return null;

  const siguiente = pasoSiguiente(PASOS_PUESTA_EN_MARCHA, idPaso);
  const anterior = pasoAnterior(PASOS_PUESTA_EN_MARCHA, idPaso);

  return (
    /* `min-h-full` + columna: sin esto el pie queda pegado al último contenido
       y, con cuatro tarjetas bajas, a media pantalla. Matías lo pidió abajo a la
       derecha. `min-h-full` y no `100vh`: en desktop el scroller es el panel de
       contenido (`.content-panel-clip`), no el body — la altura de la ventana no
       es la del área útil. */
    <div className="flex min-h-full flex-1 flex-col p-8">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          {/* Sin "Paso X de Y" y sin barra de progreso: los sacó Matías por
              pedido explícito. NO REVERTIR. El encabezado dice qué se hace en
              este paso, y nada más. El total y el número siguen calculándose en
              `progresoDePaso` (y sus tests siguen fijándolo) para cuando el
              flujo tenga que volver a mostrarlo.

              El texto sale del PASO, no de esta pantalla: con el título escrito
              acá, el paso 2 se agregaba y el `<h1>` seguía diciendo el del paso
              1 mientras abajo se dibujaba otra cosa. */}
          <h1 className="text-[22px] font-semibold text-brand-black">
            {progreso.paso.encabezado}
          </h1>
          {progreso.paso.bajada && (
            <p className="mt-1 text-[13px] leading-relaxed text-[#02062B]/[0.55]">
              {progreso.paso.bajada}
            </p>
          )}
        </div>
        <BotonPreguntaleAtlas
          pantalla="Puesta en marcha del estudio de marketing"
          // También por paso: el chat tiene que saber qué se está mirando, no
          // el primer paso del flujo para siempre.
          detalle={progreso.paso.detalleChat}
        />
      </div>

      {COMPONENTE_PASO[progreso.paso.id]({
        siguiente,
        // Avanzar es cambiar de paso, no navegar: la pantalla es una sola.
        onContinuar: (proximo) => setIdPaso(proximo.id),
        anterior,
        onVolver: (previo) => setIdPaso(previo.id),
      })}
    </div>
  );
};

export default PuestaEnMarchaPage;
